import { useState } from 'react';
import { useImportFromXmlMutation } from '../../../features/authentication/qSetApiSlice';
import CustomToast from '../../../components/Toast';

const useImportXml = (onSuccess) => {
  const [openImportXmlModal, setOpenImportXmlModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImportWaiting, setIsImportWaiting] = useState(false);
  const [importLoadingProgress, setImportLoadingProgress] = useState('0%');

  const [importFromXml] = useImportFromXmlMutation();

  const handleImportQuestionSet = () => setOpenImportXmlModal(true);

  const handleImportFileChange = (e) => {
    e.preventDefault();
    setImportFile(e.target.files[0]);
  };

  const closeModal = () => {
    setOpenImportXmlModal(false);
    setImportFile(null);
    setIsImportWaiting(false);
    setImportLoadingProgress('0%');
  };

  const resetImportState = () => {
    setIsImportWaiting(false);
    setImportLoadingProgress('0%');
  };

  const uploadImportChunk = async (chunk, currentChunk, totalChunks, fileName) => {
    try {
      const options = { fileName, fileContent: chunk, currentChunk, totalChunks };
      const response = await importFromXml(options);
      if (currentChunk === totalChunks) {
        if (response.error) {
          CustomToast(response.error.data?.error || 'Failed to import XML file.', 'error');
        } else {
          CustomToast(response.data?.message || 'Question set imported successfully.', 'success');
          closeModal();
          if (onSuccess) onSuccess();
        }
        resetImportState();
      } else {
        if (response.error) {
          CustomToast(response.error.data?.error || 'Failed to import XML file.', 'error');
          resetImportState();
          return false;
        }
        return true;
      }
    } catch (error) {
      CustomToast('Failed to import XML file.', 'error');
      console.log(error);
      resetImportState();
      return false;
    }
  };

  const handleUploadImportXml = async () => {
    if (!importFile) return;
    const fileExtension = importFile.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'xml') {
      CustomToast('Invalid file type. Please select an XML file.', 'warning');
      return;
    }
    setIsImportWaiting(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const content = e.target.result;
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(content, 'text/xml');
      const xmlString = new XMLSerializer().serializeToString(xmlDoc);

      const chunkSize = 10 * 1024 * 1024; // 10MB chunks
      const totalChunks = Math.ceil(xmlString.length / chunkSize);

      if (totalChunks <= 1)
        setImportLoadingProgress('Importing');

      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = (i + 1) * chunkSize;
        const chunk = btoa(unescape(encodeURIComponent(xmlString.substring(start, end))));
        if (totalChunks > 1)
          setImportLoadingProgress(Math.round(((i + 1) / totalChunks) * 100) + '%');
        if (!(await uploadImportChunk(chunk, i + 1, totalChunks, importFile.name))) break;
      }
    };
    reader.onerror = () => {
      CustomToast('Failed to read XML file.', 'error');
      resetImportState();
    };
    reader.onabort = () => {
      CustomToast('XML file import was cancelled.', 'warning');
      resetImportState();
    };
    reader.readAsText(importFile);
  };

  return {
    openImportXmlModal,
    importFile,
    isImportWaiting,
    importLoadingProgress,
    handleImportQuestionSet,
    handleImportFileChange,
    handleUploadImportXml,
    closeModal
  };
};

export default useImportXml;
